// What every «Betrieb» change shares: the reason it carries and the refusals
// of the runtime API in words.

import { t, tOr, type TranslationKey } from "@/i18n";

/** The backend's bounds for the reason every change carries. */
export const MIN_REASON = 10;
export const MAX_REASON = 500;

const ERROR_KEYS: Record<string, TranslationKey> = {
  version_conflict: "ops.error.conflict",
  runtime_unreadable: "ops.error.unreadable",
  wrong_password: "ops.error.password",
  confirm_text_mismatch: "ops.error.confirm",
  wipe_locked: "ops.error.locked",
  wipe_disabled: "ops.error.wipeDisabled",
  wipe_incomplete: "ops.error.wipeIncomplete",
  unknown_customer: "ops.error.unknownCustomer",
  invalid_quiet_hours: "ops.error.quietHours",
};

/** Whether a reason passes the backend's bounds. */
export function reasonValid(reason: string): boolean {
  const text = reason.trim();
  return text.length >= MIN_REASON && text.length <= MAX_REASON;
}

export interface ErrorDetail {
  error?: unknown;
  problems?: unknown;
  kind?: unknown;
  detail?: unknown;
  // A refused Fragen-Check list: why, and the bound it broke.
  reason?: unknown;
  max_rows?: unknown;
  // A session delete that stopped short: at which step (the document itself,
  // or the purge of turns and events after it) and what it removed before.
  stage?: unknown;
  turns_deleted?: unknown;
  events_deleted?: unknown;
}

/** The HTTP status of a failed call; null when no response came (a network error). */
export function httpStatus(err: unknown): number | null {
  return typeof err === "object" && err !== null && "status" in err && typeof err.status === "number"
    ? err.status
    : null;
}

// A refusal of the runtime API arrives as {"detail": {"error": <code>, …}}; a
// validation error's `detail` is a list and names no code.
export function errorDetail(err: unknown): ErrorDetail {
  const detail = (err as { body?: { detail?: unknown } } | null)?.body?.detail;
  return detail && typeof detail === "object" && !Array.isArray(detail)
    ? (detail as ErrorDetail)
    : {};
}

/** A failed change in words: the known refusals by name, else the HTTP error. */
export function runtimeErrorMessage(err: unknown): string {
  const detail = errorDetail(err);
  if (detail.error === "invalid_switch") {
    return t("ops.error.invalid", { problems: String(detail.problems ?? "") });
  }
  if (detail.error === "invalid_customer") {
    return t("ops.error.customer", { problems: String(detail.problems ?? "") });
  }
  if (detail.error === "probe_failed") {
    const kind = String(detail.kind ?? "");
    return t("ops.error.probe", {
      kind: tOr(`ops.probe.${kind}`, kind),
      detail: String(detail.detail ?? ""),
    });
  }
  const key = typeof detail.error === "string" ? ERROR_KEYS[detail.error] : undefined;
  if (key) return t(key);
  return err instanceof Error ? err.message : String(err);
}
